import { useState, useEffect, useRef, useCallback } from "react";

export const useWebSocket3D = () => {

  const [isConnected, setIsConnected] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [jobProgress, setJobProgress] = useState(0);
  const [cloudUrl, setCloudUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [systemStats, setSystemStats] = useState({
    clients: 0,
    workers: 0,
    queue: 0,
  });
  const [jobId, setJobId] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  const [processingStage, setProcessingStage] = useState(null); // Track stage based on worker logs

  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const handleMessageRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const shouldReconnectRef = useRef(false); // Only reconnect if user initiated connection
  const targetJobIdRef = useRef(null); // Store target job_id for reconnection

  // ---------------------------
  //  SAFE BACKEND + WS URL
  // ---------------------------
  const getPythonHttpBase = () => {
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    if (isLocal) {
      return "http://localhost:8000";
    }

    return process.env.REACT_APP_PYTHON_BASE_URL_PRODUCTION || "https://demopy.3d-medico.com";
  };

  const getBackendUrl = () => {
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    if (isLocal) {
      return "ws://localhost:8000"; // local FastAPI WebSocket
    }

    // Production: env-var bestimmt das Python-Backend (Live vs. Demo). getWebSocketUrl()
    // konvertiert dann https:// → wss:// für die WebSocket-Verbindung.
    return process.env.REACT_APP_PYTHON_BASE_URL_PRODUCTION || "https://demopy.3d-medico.com";
  };

  const getWebSocketUrl = (targetJobId = null) => {
    const backend = getBackendUrl().replace("https://", "wss://").replace("http://", "ws://");

    // All environments: Uses cookies for authentication
    // If targetJobId is provided, connect to that specific session (for viewing logs)
    if (targetJobId) {
      return `${backend}/3d-service/connect?job_id=${encodeURIComponent(targetJobId)}`;
    }
    return `${backend}/3d-service/connect`;
  };

  // ---------------------------
  //  HANDLE MESSAGES
  // ---------------------------
  const handleMessage = useCallback((msg) => {
    // 0. Filter out "No job is currently running" noise immediately.
    // This message is sent by the backend when it's idle or a session is closed.
    if (msg.status_type === "error" && msg.message && msg.message.includes("No job is currently running")) {
      return;
    }

    // 1. Defensively clone message content to avoid mutation issues
    const content = msg.content ? { ...msg.content } : null;
    const msgToProcess = { ...msg, content };

    // 2. Determine base URL using environment variable (production) or local fallback
    const pythonHttpBase = process.env.NODE_ENV === "production"
      ? (process.env.REACT_APP_PYTHON_BASE_URL_PRODUCTION || "https://demopy.3d-medico.com")
      : "http://localhost:8000";

    // 3. Transformation logic for relative paths
    if (content) {
      // Handle cloud_url (Download)
      if (content.cloud_url && !content.cloud_url.startsWith("http")) {
        let path = content.cloud_url;
        if (!path.startsWith("/")) path = "/" + path;

        // Transform /file-explorer/ -> /download-all-zip/ and append /original
        let processedPath = path.replace("/file-explorer/", "/download-all-zip/");
        if (!processedPath.endsWith("/")) processedPath += "/";
        if (!processedPath.includes("/original")) {
          processedPath += "original";
        }
        content.cloud_url = pythonHttpBase + processedPath;
      }

      // Handle preview_url
      if (content.preview_url && !content.preview_url.startsWith("http")) {
        let path = content.preview_url;
        if (!path.startsWith("/")) path = "/" + path;
        content.preview_url = pythonHttpBase + path;
      }
    }

    // 4. Update states with the processed (transformed) message
    setLastMessage(msgToProcess);

    if (content?.job_id) {
      setJobId(content.job_id);
    }

    if (content?.client_num !== undefined) {
      setSystemStats((prev) => ({ ...prev, clients: content.client_num }));
    }

    if (content?.worker_num !== undefined) {
      setSystemStats((prev) => ({ ...prev, workers: content.worker_num }));
    }

    if (content?.queue_length !== undefined) {
      setSystemStats((prev) => ({ ...prev, queue: content.queue_length }));
    }

    if (content?.upload_percentage !== undefined) {
      setUploadProgress(content.upload_percentage);
    }

    if (content?.job_percentage !== undefined) {
      setJobProgress(content.job_percentage);
    }

    if (content?.job_status) {
      setJobStatus(content.job_status);
    }

    // Detect processing stage from log messages
    const message = msgToProcess.message || "";
    if (message) {
      // Stage: Vorbereiten (Preparing) - sending file to worker
      if (message.includes("Sending Job to Worker") ||
          message.includes("WORKER: Receiving progress:") ||
          message.includes("WORKER: File meta has been set")) {
        setProcessingStage("vorbereiten");
      }
      // Stage: Photogrammetrie - actual 3D reconstruction processing
      else if (message.includes("WORKER: Photogrammetry") ||
               message.includes("WORKER: Extracting") ||
               message.includes("WORKER: Feature") ||
               message.includes("WORKER: Matching") ||
               message.includes("WORKER: Reconstruction") ||
               message.includes("WORKER: Mesh") ||
               message.includes("WORKER: Texture") ||
               message.includes("WORKER: Processing") ||
               message.includes("WORKER: Densifying") ||
               message.includes("WORKER: Depth") ||
               (message.includes("WORKER:") && message.includes("%") && !message.includes("Receiving progress") && !message.includes("Uploading"))) {
        setProcessingStage("photogrammetrie");
      }
      // Stage: Abschließen (Finishing) - receiving original 3D model files from worker
      else if ((message.includes("Receiving file:") && message.includes("/original/")) ||
               (message.includes("WORKER: Uploading") &&
                (message.includes("baked_mesh") || message.includes(".png") || message.includes(".obj") || message.includes(".usda")) &&
                !message.includes("scaled_") && !message.includes("point_cloud"))) {
        setProcessingStage("abschliessen");
      }
      // Stage: Skalieren (Scaling) - scaling and uploading scaled files
      else if (message.includes("WORKER: Start scaling") ||
               message.includes("WORKER: Scaling completed") ||
               message.includes("WORKER: Uploading Scaled") ||
               (message.includes("WORKER: Uploading") &&
                (message.includes("scaled_") || message.includes("point_cloud"))) ||
               (message.includes("Receiving file:") &&
                (message.includes("/scaled/") || message.includes("/plot/")))) {
        setProcessingStage("skalieren");
      }
      // Stage: Cloud upload
      else if (message.includes("Uploading to cloud storage") ||
               message.includes("Uploading raw data") ||
               message.includes("Uploading processed results")) {
        setProcessingStage("cloud-upload");
      }
    }

    // Completed or Review - extract URLs
    if (msgToProcess.status_type === "success" && (content?.job_status === "completed" || content?.job_status === "review")) {
      if (content.cloud_url) setCloudUrl(content.cloud_url);
      if (content.preview_url) setPreviewUrl(content.preview_url);
      setJobStatus(content.job_status);
    }

    if (msgToProcess.status_type === "error") {
      console.error("WS ERROR:", msgToProcess.message);
      setError(msgToProcess.message || "Processing failed");
      setJobStatus("failed");
    }
  }, []);

  // Sync ref with handleMessage to avoid closure issues in onmessage
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  // ---------------------------
  //  AUTO-RECONNECT LOGIC
  // ---------------------------
  const MAX_RECONNECT_ATTEMPTS = 5;
  const BASE_RECONNECT_DELAY = 5000; // 5 seconds

  const scheduleReconnect = useCallback(() => {
    if (!shouldReconnectRef.current) {
      console.log("🔌 [WS] Auto-reconnect disabled - not attempting");
      return;
    }

    if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.log(`❌ [WS] Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached`);
      setError("Verbindung verloren. Bitte Seite neu laden.");
      return;
    }

    const delay = BASE_RECONNECT_DELAY;
    reconnectAttemptRef.current += 1;

    console.log(`🔄 [WS] Scheduling reconnect attempt ${reconnectAttemptRef.current}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`);

    reconnectTimeoutRef.current = setTimeout(() => {
      console.log(`🔄 [WS] Attempting reconnect...`);
      // Use stored job_id for reconnection
      connect(targetJobIdRef.current).catch((err) => {
        console.error("🔄 [WS] Reconnect failed:", err);
        scheduleReconnect(); // Try again
      });
    }, delay);
  }, []);

  // ---------------------------
  //  CONNECT
  // ---------------------------
  // Optional targetJobId: If provided, connects to that specific session (for viewing logs of a specific job)
  const connect = useCallback((targetJobId = null) => {
    // Store job_id for potential reconnection
    targetJobIdRef.current = targetJobId;

    return new Promise((resolve, reject) => {
      // Prevent duplicate or overlapping connections
      if (wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING)) {
        resolve();
        return;
      }

      // Clear any pending reconnect
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Enable auto-reconnect when user initiates connection
      shouldReconnectRef.current = true;

      const url = getWebSocketUrl(targetJobId);
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        console.log("✅ [WS] Connected");
        setIsConnected(true);
        setError(null);
        reconnectAttemptRef.current = 0; // Reset reconnect counter on successful connection
        resolve();
      };

      ws.onerror = (err) => {
        console.error("🔥 WS Error:", err);
        setError("WebSocket error");
        reject(err);
      };

      ws.onclose = (event) => {
        console.log(`🔌 [WS] Connection closed (code: ${event.code}, reason: ${event.reason || "none"})`);
        setIsConnected(false);
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;

        // Auto-reconnect if connection was not intentionally closed
        // Code 1000 = normal closure, 1001 = going away (page unload)
        if (shouldReconnectRef.current && event.code !== 1000 && event.code !== 1001) {
          scheduleReconnect();
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (handleMessageRef.current) {
            handleMessageRef.current(msg);
          }
        } catch (err) {
          console.error("Invalid WS JSON:", event.data);
        }
      };
    });
  }, [scheduleReconnect]);


  // ---------------------------
  //  DISCONNECT
  // ---------------------------
  const disconnect = useCallback(() => {
    // Disable auto-reconnect when user intentionally disconnects
    shouldReconnectRef.current = false;

    // Clear any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, "User disconnected"); // Normal closure
      wsRef.current = null;
    }

    clearInterval(heartbeatRef.current);
    reconnectAttemptRef.current = 0;

    // Reset all state to initial values
    setIsConnected(false);
    setJobStatus(null);
    setUploadProgress(0);
    setJobProgress(0);
    setCloudUrl(null);
    setPreviewUrl(null);
    setError(null);
    setJobId(null);
    setLastMessage(null);
    setProcessingStage(null);
  }, []);

  // ---------------------------
  //  SEND HELPERS
  // ---------------------------
  const sendJson = (data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    } else {
      console.error('❌ Cannot send: WebSocket not connected');
    }
  };

  const sendBuffer = (buffer) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(buffer);
    } else {
      console.error('❌ Cannot send: WebSocket not connected');
    }
  };

  // ---------------------------
  //  UPLOAD (CHUNKED)
  // ---------------------------
  const uploadFile = async (zipFile, category) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError("WebSocket not connected");
      return;
    }

    setUploadProgress(0);
    setJobProgress(0);
    setJobStatus("uploading");
    setProcessingStage(null); // Reset processing stage for new upload

    wsRef.current.send(
      JSON.stringify({
        action: "upload_init",
        data: { filename: zipFile.name, size: zipFile.size, category },
      })
    );

    const CHUNK_SIZE = 64 * 1024;
    let offset = 0;

    while (offset < zipFile.size) {
      const chunk = zipFile.slice(offset, offset + CHUNK_SIZE);
      const buffer = await chunk.arrayBuffer();
      wsRef.current.send(buffer);
      offset += CHUNK_SIZE;

      setUploadProgress(Math.floor((offset / zipFile.size) * 100));
    }

    // Send start_process after upload completes
    wsRef.current.send(JSON.stringify({ action: "start_process" }));
    setJobStatus("file_received");
  };

  // ---------------------------
  //  CLEANUP ON UNMOUNT
  // ---------------------------
  useEffect(() => {
    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      disconnect();
    };
  }, [disconnect]);

  // ---------------------------
  //  RETURN
  // ---------------------------
  return {
    isConnected,
    jobStatus,
    uploadProgress,
    jobProgress,
    cloudUrl,
    previewUrl,
    jobId,
    lastMessage,
    error,
    systemStats,
    processingStage,
    connect,
    disconnect,
    uploadFile,
    sendJson,
    sendBuffer,
    connected: isConnected,
  };
};
